import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

export default function OrganizationPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [equipmentDropdownOpen, setEquipmentDropdownOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [mobileEquipmentOpen, setMobileEquipmentOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [ceoVisible, setCeoVisible] = useState(false);
  const [officeVisible, setOfficeVisible] = useState(false);
  const [fieldVisible, setFieldVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState<Set<number>>(new Set());

  const ceoRef = useRef<HTMLDivElement>(null);
  const officeRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  // 조직도 트리 애니메이션
  useEffect(() => {
    const timer1 = setTimeout(() => setCeoVisible(true), 200);
    const timer2 = setTimeout(() => setOfficeVisible(true), 600);
    const timer3 = setTimeout(() => setFieldVisible(true), 900);
    return () => { clearTimeout(timer1); clearTimeout(timer2); clearTimeout(timer3); };
  }, []);

  // 부서 카드 스크롤 애니메이션
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-card-index'));
            setTimeout(() => {
              setCardsVisible(prev => new Set([...prev, index]));
            }, index * 120);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    cardRefs.current.forEach((ref) => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, []);

  const handleGoToMap = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('map');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const navLinks = [
    {
      href: '/#about', label: '회사소개', type: 'dropdown', subMenu: [
        { href: '/#about', label: '회사소개', scrollTo: null },
        { href: '/history', label: '연혁', scrollTo: null },
        { href: '/organization', label: '조직도', scrollTo: null },
        { href: null, label: '오시는 길', scrollTo: 'map' },
      ]
    },
    { href: '/#process', label: '생산공정', type: 'scroll' },
    {
      href: '#equipment', label: '보유장비', type: 'dropdown', subMenu: [
        { href: '/equipment/production', label: '생산장비', scrollTo: null },
        { href: '/equipment/welding', label: '용접장비', scrollTo: null },
        { href: '/equipment/capability', label: '가공능력', scrollTo: null },
        { href: '/equipment/materials', label: '가공 가능자재', scrollTo: null },
      ]
    },
    {
      href: '#products', label: '제조물품', type: 'dropdown', subMenu: [
        { href: '/gallery', label: '갤러리', scrollTo: null },
      ]
    },
  ];

  const deptCards = [
    {
      icon: 'ri-pencil-ruler-2-line',
      color: 'orange',
      dept: '설계팀',
      tag: '사무실',
      desc: 'CAD 도면 설계 및 레이저·NCT 가공 프로그램 작성, 공정 계획 수립을 담당합니다. 고객 도면을 분석하여 최적의 가공 방법을 제안합니다.',
      tasks: ['CAD 도면 설계', '레이저 프로그램 작성', '공정 계획 수립', '고객 도면 검토']
    },
    {
      icon: 'ri-file-list-3-line',
      color: 'orange',
      dept: '관리팀',
      tag: '사무실',
      desc: '수주 관리, 납기 일정 조율, 품질 관리 및 고객 대응을 담당합니다. 전체 생산 흐름을 조율하여 납기 준수율을 높입니다.',
      tasks: ['수주·납기 관리', '품질 관리', '고객 대응', '원자재 구매']
    },
    {
      icon: 'ri-flashlight-line',
      color: 'gray',
      dept: '레이저부',
      tag: '현장',
      desc: 'BYSTRONIC 레이저 장비를 활용한 고정밀 레이저 커팅 및 NCT 가공을 담당합니다. 다양한 소재와 두께에 맞는 최적의 가공 조건으로 정밀한 절단 품질을 보장합니다.',
      tasks: ['레이저 커팅', 'NCT 가공', '프로그램 운용', '소재 관리']
    },
    {
      icon: 'ri-scissors-cut-line',
      color: 'gray',
      dept: '절곡부',
      tag: '현장',
      desc: '절곡(벤딩) 및 TAP 가공을 담당합니다. 정밀한 치수 관리와 반복 정밀도를 통해 고품질의 절곡 제품을 생산합니다.',
      tasks: ['절곡(벤딩)', 'TAP 가공', '치수 검측', '지그 관리']
    },
    {
      icon: 'ri-fire-line',
      color: 'gray',
      dept: '용접부',
      tag: '현장',
      desc: 'MIG·TIG·CO₂·레이저 용접 전 공정을 담당합니다. 소재와 두께에 따라 최적의 용접 방식을 선택하여 강도와 외관 품질을 확보합니다.',
      tasks: ['MIG 용접', 'TIG 용접', 'CO₂ 용접', '레이저 용접']
    },
    {
      icon: 'ri-settings-3-line',
      color: 'gray',
      dept: '조립및검사부',
      tag: '현장',
      desc: '부품 조립, 완제품 품질 검사, 포장 및 납품 준비를 담당합니다. 최종 출하 전 전수 검사를 통해 불량 없는 제품을 납품합니다.',
      tasks: ['부품 조립', '완제품 검사', '포장 작업', '납품 준비']
    },
  ];

  return (
    <>
      <SEOHead
        title="조직도 | NS-TECH 판금가공"
        description="NS-TECH의 체계적인 조직 구성. 설계팀, 관리팀, 레이저부, 절곡부, 용접부, 조립검사부 등 전문화된 부서로 최고의 품질과 서비스를 제공합니다."
        keywords="NS-TECH 조직도, 회사 조직, 설계팀, 생산부서, 품질관리, 전문 인력, 제조 조직"
        ogType="website"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: 'NS-TECH 조직도',
            description: '체계적인 조직 구성으로 설계부터 납품까지 원스톱 서비스 제공',
            url: `${import.meta.env.VITE_SITE_URL}/organization`,
            mainEntity: {
              '@type': 'Organization',
              name: 'NS-TECH',
              url: import.meta.env.VITE_SITE_URL,
              numberOfEmployees: {
                '@type': 'QuantitativeValue',
                value: '6개 전문 부서',
              },
              department: [
                { '@type': 'Organization', name: '설계팀' },
                { '@type': 'Organization', name: '관리팀' },
                { '@type': 'Organization', name: '레이저부' },
                { '@type': 'Organization', name: '절곡부' },
                { '@type': 'Organization', name: '용접부' },
                { '@type': 'Organization', name: '조립및검사부' },
              ],
            },
          },
        ]}
      />
      <div className="min-h-screen bg-white">
        {/* 네비게이션 */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
          <div className="w-full px-4 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="hidden lg:flex items-center gap-2">
                <Link to="/" className="flex items-center gap-2 shrink-0 mr-2">
                  <img
                    src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
                    alt="NS-TECH Logo"
                    className="h-12 w-auto object-contain"
                  />
                </Link>
                {navLinks.map((link) => (
                  link.type === 'dropdown' ? (
                    <div
                      key={link.label}
                      className="relative"
                      onMouseEnter={() => {
                        if (link.label === '보유장비') setEquipmentDropdownOpen(true);
                        if (link.label === '제조물품') setProductsDropdownOpen(true);
                        if (link.label === '회사소개') setAboutDropdownOpen(true);
                      }}
                      onMouseLeave={() => {
                        if (link.label === '보유장비') setEquipmentDropdownOpen(false);
                        if (link.label === '제조물품') setProductsDropdownOpen(false);
                        if (link.label === '회사소개') setAboutDropdownOpen(false);
                      }}
                    >
                      <button className={`text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md flex items-center gap-1 cursor-pointer ${
                        link.label === '회사소개' ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
                      }`}>
                        {link.label}
                        <i className={`ri-arrow-down-s-line transition-transform ${
                          (link.label === '보유장비' && equipmentDropdownOpen) ||
                          (link.label === '제조물품' && productsDropdownOpen) ||
                          (link.label === '회사소개' && aboutDropdownOpen)
                            ? 'rotate-180' : ''
                        }`}></i>
                      </button>
                      {((link.label === '보유장비' && equipmentDropdownOpen) ||
                        (link.label === '제조물품' && productsDropdownOpen) ||
                        (link.label === '회사소개' && aboutDropdownOpen)) && (
                        <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-lg py-2 min-w-[160px] border border-gray-100">
                          {link.subMenu?.map((subLink) => (
                            subLink.scrollTo === 'map' ? (
                              <button
                                key={subLink.label}
                                onClick={handleGoToMap}
                                className="block w-full text-left px-4 py-2 text-sm hover:text-orange-500 hover:bg-orange-50 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                              >
                                {subLink.label}
                              </button>
                            ) : (
                              <Link
                                key={subLink.href}
                                to={subLink.href!}
                                className={`block px-4 py-2 text-sm hover:text-orange-500 hover:bg-orange-50 transition-colors whitespace-nowrap ${
                                  subLink.href === '/organization' ? 'text-orange-500 font-semibold' : 'text-gray-700'
                                }`}
                              >
                                {subLink.label}
                              </Link>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      key={link.href}
                      to={link.href!}
                      className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                    >
                      {link.label}
                    </Link>
                  )
                ))}
              </div>

              <div className="flex lg:hidden items-center justify-between w-full">
                <Link to="/" className="flex items-center gap-2 shrink-0">
                  <img
                    src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
                    alt="NS-TECH Logo"
                    className="h-10 w-auto object-contain"
                  />
                </Link>
                <button
                  className="flex flex-col justify-center items-center w-10 h-10 gap-1.5 cursor-pointer relative z-[60]"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="메뉴 열기"
                >
                  <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                  <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
                  <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                </button>
              </div>

              <div className="hidden lg:flex items-center">
                <button
                  onClick={() => setShowContactModal(true)}
                  className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50 cursor-pointer"
                >
                  고객지원
                </button>
              </div>
            </div>
          </div>

          <div
            className={`lg:hidden fixed inset-0 bg-black/50 transition-opacity duration-300 z-40 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            onClick={() => setMobileMenuOpen(false)}
          ></div>

          <div className={`lg:hidden fixed top-[60px] left-0 right-0 transition-all duration-300 ease-in-out z-50 ${mobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'}`}>
            <div className="bg-white shadow-xl border-t border-gray-100 px-5 py-3 flex flex-col gap-1">
              {navLinks.map((link) => (
                link.type === 'dropdown' ? (
                  <div key={link.label}>
                    <button
                      onClick={() => {
                        if (link.label === '보유장비') setMobileEquipmentOpen(!mobileEquipmentOpen);
                        if (link.label === '제조물품') setMobileProductsOpen(!mobileProductsOpen);
                        if (link.label === '회사소개') setMobileAboutOpen(!mobileAboutOpen);
                      }}
                      className="w-full text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-left text-gray-700 hover:text-orange-500 hover:bg-orange-50 flex items-center justify-between"
                    >
                      {link.label}
                      <i className={`ri-arrow-down-s-line transition-transform ${
                        (link.label === '보유장비' && mobileEquipmentOpen) ||
                        (link.label === '제조물품' && mobileProductsOpen) ||
                        (link.label === '회사소개' && mobileAboutOpen)
                          ? 'rotate-180' : ''
                      }`}></i>
                    </button>
                    {((link.label === '보유장비' && mobileEquipmentOpen) ||
                      (link.label === '제조물품' && mobileProductsOpen) ||
                      (link.label === '회사소개' && mobileAboutOpen)) && (
                      <div className="pl-4 mt-1 space-y-1">
                        {link.subMenu?.map((subLink) => (
                          subLink.scrollTo === 'map' ? (
                            <button
                              key={subLink.label}
                              onClick={() => { setMobileMenuOpen(false); handleGoToMap(); }}
                              className="block w-full text-left text-sm font-medium py-2 px-4 rounded-lg transition-all cursor-pointer text-gray-600 hover:text-orange-500 hover:bg-orange-50"
                            >
                              {subLink.label}
                            </button>
                          ) : (
                            <Link
                              key={subLink.href}
                              to={subLink.href!}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block text-sm font-medium py-2 px-4 rounded-lg transition-all cursor-pointer text-gray-600 hover:text-orange-500 hover:bg-orange-50"
                            >
                              {subLink.label}
                            </Link>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href!}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                  >
                    {link.label}
                  </Link>
                )
              ))}
              <button
                onClick={() => { setMobileMenuOpen(false); setShowContactModal(true); }}
                className="w-full text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-left text-gray-700 hover:text-orange-500 hover:bg-orange-50"
              >
                고객지원
              </button>
            </div>
          </div>
        </nav>

        {/* 헤더 배너 */}
        <div className="pt-20 pb-0">
          <div className="relative h-52 md:h-72 overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/dc266d8f09fede8a1c4b34a29c480aca.jpg"
              alt="NS-TECH 조직도"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <span className="text-orange-400 text-xs font-semibold uppercase tracking-widest mb-2 animate-[fadeIn_0.6s_ease-out]">Organization Chart</span>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 animate-[fadeIn_0.8s_ease-out]">NS-TECH 조직도</h1>
              <p className="text-white/80 text-sm md:text-base max-w-xl animate-[fadeIn_1s_ease-out]">
                체계적인 조직 구성으로 최고의 품질과 서비스를 제공합니다.
              </p>
            </div>
          </div>
        </div>

        {/* 핵심 수치 배너 */}
        <div className="bg-orange-500 py-7">
          <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {[
              { icon: 'ri-team-line', value: '6개', label: '전문 부서' },
              { icon: 'ri-user-line', value: '2개', label: '사무 부서' },
              { icon: 'ri-tools-line', value: '4개', label: '현장 부서' },
              { icon: 'ri-award-line', value: '14년+', label: '전문 경력' },
            ].map((stat, i) => (
              <div key={i} className="flex flex-col items-center text-center transition-all duration-500 hover:scale-110">
                <div className="w-11 h-11 flex items-center justify-center bg-white/20 rounded-full mb-2 animate-[pulse_2.5s_ease-in-out_infinite]">
                  <i className={`${stat.icon} text-white text-lg`}></i>
                </div>
                <p className="text-white text-2xl font-bold">{stat.value}</p>
                <p className="text-orange-100 text-xs mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 조직도 본문 */}
        <div className="py-16 md:py-24 px-5 md:px-16 bg-gray-50">
          <div className="max-w-6xl mx-auto">

            <div className="text-center mb-12 md:mb-16">
              <span className="text-orange-500 text-xs font-semibold uppercase tracking-widest">Structure</span>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mt-2 mb-3">조직 구성</h2>
              <div className="w-10 h-1 bg-orange-500 mx-auto rounded-full"></div>
              <p className="text-gray-500 text-sm mt-4 max-w-xl mx-auto">
                NS-TECH는 전문화된 부서 구성으로 설계부터 납품까지 원스톱 서비스를 제공합니다.
              </p>
            </div>

            {/* 조직도 트리 */}
            <div className="flex flex-col items-center gap-0">

              {/* 대표 */}
              <div
                ref={ceoRef}
                className={`flex justify-center mb-2 transition-all duration-700 ${ceoVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}`}
              >
                <div className="relative bg-orange-500 text-white rounded-2xl px-12 py-5 shadow-xl text-center min-w-[220px] hover:scale-105 transition-transform duration-300 cursor-default">
                  {/* 글로우 효과 */}
                  <div className="absolute inset-0 rounded-2xl bg-orange-400/30 animate-[pulse_2s_ease-in-out_infinite] -z-10 scale-110 blur-md"></div>
                  <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-2 animate-[pulse_2.5s_ease-in-out_infinite]">
                    <i className="ri-user-star-line text-white text-2xl"></i>
                  </div>
                  <p className="text-xs font-semibold text-orange-100 uppercase tracking-widest mb-0.5">CEO</p>
                  <p className="text-xl font-bold">대표</p>
                  <div className="mt-2 flex items-center justify-center gap-1">
                    <i className="ri-star-fill text-yellow-300 text-xs animate-[pulse_1.5s_ease-in-out_infinite]"></i>
                    <span className="text-orange-100 text-xs">NS-TECH</span>
                    <i className="ri-star-fill text-yellow-300 text-xs animate-[pulse_1.5s_ease-in-out_infinite]"></i>
                  </div>
                </div>
              </div>

              {/* 연결선 */}
              <div className={`flex flex-col items-center mb-2 transition-all duration-500 delay-300 ${ceoVisible ? 'opacity-100' : 'opacity-0'}`}>
                <div className="w-0.5 h-8 bg-gradient-to-b from-orange-400 to-orange-300"></div>
                <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-full border-2 border-orange-300 animate-[bounce_2s_ease-in-out_infinite]">
                  <i className="ri-arrow-down-line text-orange-500 text-lg"></i>
                </div>
                <div className="w-0.5 h-4 bg-orange-300"></div>
              </div>

              {/* 사무실 / 현장 */}
              <div className="w-full flex flex-col md:flex-row justify-center items-stretch gap-6 md:gap-8 max-w-5xl mx-auto">

                {/* 사무실 */}
                <div
                  ref={officeRef}
                  className={`flex-1 w-full md:w-auto transition-all duration-700 delay-500 ${officeVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-16'}`}
                >
                  <div className="bg-orange-50 border-2 border-orange-300 rounded-2xl overflow-hidden shadow-lg h-full flex flex-col hover:shadow-xl hover:border-orange-400 transition-all duration-300">
                    <div className="bg-orange-400 text-white px-6 py-4 flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full shrink-0 animate-[pulse_2s_ease-in-out_infinite]">
                        <i className="ri-building-line text-white text-xl"></i>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-orange-100 uppercase tracking-widest">Office</p>
                        <p className="text-lg font-bold">사무실</p>
                      </div>
                      <div className="ml-auto">
                        <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">2개 부서</span>
                      </div>
                    </div>
                    <div className="p-4 flex flex-col gap-3 flex-1">
                      {[
                        { icon: 'ri-pencil-ruler-2-line', label: '설계', sub: 'Design', tasks: ['CAD 도면 설계', '레이저 프로그램', '공정 계획'] },
                        { icon: 'ri-file-list-3-line', label: '관리', sub: 'Management', tasks: ['수주·납기 관리', '품질 관리', '고객 대응'] },
                      ].map((dept, di) => (
                        <div
                          key={dept.label}
                          className="bg-white rounded-xl px-4 py-3 border border-orange-100 shadow-sm flex-1 flex flex-col justify-center hover:border-orange-300 hover:shadow-md hover:scale-[1.02] transition-all duration-300 cursor-default"
                          style={{ transitionDelay: `${di * 100}ms` }}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-9 h-9 flex items-center justify-center bg-orange-100 rounded-full shrink-0">
                              <i className={`${dept.icon} text-orange-500 text-base`}></i>
                            </div>
                            <div>
                              <p className="text-xs text-orange-400 font-semibold">{dept.sub}</p>
                              <p className="text-sm font-bold text-gray-800">{dept.label}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1.5 pl-0 md:pl-12">
                            {dept.tasks.map((task) => (
                              <span key={task} className="text-xs bg-orange-50 border border-orange-200 text-orange-600 rounded-full px-2.5 py-0.5">{task}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 현장 */}
                <div
                  ref={fieldRef}
                  className={`flex-1 w-full md:w-auto transition-all duration-700 delay-700 ${fieldVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-16'}`}
                >
                  <div className="bg-gray-50 border-2 border-gray-300 rounded-2xl overflow-hidden shadow-lg h-full flex flex-col hover:shadow-xl hover:border-gray-400 transition-all duration-300">
                    <div className="bg-gray-600 text-white px-6 py-4 flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full shrink-0 animate-[pulse_2s_ease-in-out_infinite]">
                        <i className="ri-tools-line text-white text-xl"></i>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-300 uppercase tracking-widest">Field</p>
                        <p className="text-lg font-bold">현장</p>
                      </div>
                      <div className="ml-auto">
                        <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">4개 부서</span>
                      </div>
                    </div>
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
                      {[
                        { icon: 'ri-flashlight-line', label: '레이저부', sub: 'Laser', tasks: ['레이저 커팅', 'NCT 가공', '프로그램 운용'] },
                        { icon: 'ri-scissors-cut-line', label: '절곡부', sub: 'Bending', tasks: ['절곡 작업', 'TAP 가공', '치수 검측'] },
                        { icon: 'ri-fire-line', label: '용접부', sub: 'Welding', tasks: ['MIG/TIG 용접', 'CO₂ 용접', '레이저 용접'] },
                        { icon: 'ri-settings-3-line', label: '조립및검사부', sub: 'Assembly', tasks: ['부품 조립', '품질 검사', '포장·납품'] },
                      ].map((dept, di) => (
                        <div
                          key={dept.label}
                          className="bg-white rounded-xl px-3 py-3 border border-gray-100 shadow-sm flex flex-col justify-center hover:border-gray-300 hover:shadow-md hover:scale-[1.02] transition-all duration-300 cursor-default"
                          style={{ transitionDelay: `${di * 80}ms` }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full shrink-0">
                              <i className={`${dept.icon} text-gray-600 text-base`}></i>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 font-semibold">{dept.sub}</p>
                              <p className="text-sm font-bold text-gray-800 whitespace-nowrap">{dept.label}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1 pl-0 sm:pl-10">
                            {dept.tasks.map((task) => (
                              <span key={task} className="text-xs bg-gray-50 border border-gray-200 text-gray-600 rounded-full px-2 py-0.5">{task}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* 생산 흐름 프로세스 */}
            <div className="mt-20 md:mt-24">
              <div className="text-center mb-10">
                <span className="text-orange-500 text-xs font-semibold uppercase tracking-widest">Workflow</span>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-2">원스톱 생산 흐름</h3>
                <div className="w-8 h-1 bg-orange-500 mx-auto rounded-full mt-2"></div>
              </div>
              <div className="flex flex-wrap justify-center items-center gap-2 md:gap-0">
                {[
                  { icon: 'ri-pencil-ruler-2-line', step: '01', label: 'CAD 설계', color: 'orange' },
                  { icon: 'ri-flashlight-line', step: '02', label: '레이저 가공', color: 'gray' },
                  { icon: 'ri-scissors-cut-line', step: '03', label: '절곡', color: 'gray' },
                  { icon: 'ri-fire-line', step: '04', label: '용접', color: 'gray' },
                  { icon: 'ri-settings-3-line', step: '05', label: '조립·검사', color: 'gray' },
                  { icon: 'ri-truck-line', step: '06', label: '납품', color: 'orange' },
                ].map((step, i) => (
                  <div key={i} className="flex items-center">
                    <div className={`flex flex-col items-center group cursor-default transition-all duration-300 hover:scale-110 ${
                      step.color === 'orange' ? '' : ''
                    }`}>
                      <div className={`w-16 h-16 rounded-2xl flex flex-col items-center justify-center shadow-md transition-all duration-300 group-hover:shadow-xl ${
                        step.color === 'orange'
                          ? 'bg-orange-500 text-white group-hover:bg-orange-600'
                          : 'bg-white border-2 border-gray-200 text-gray-600 group-hover:border-orange-300 group-hover:text-orange-500'
                      }`}>
                        <i className={`${step.icon} text-xl`}></i>
                        <span className="text-[10px] font-bold mt-0.5 opacity-70">{step.step}</span>
                      </div>
                      <p className={`text-xs font-semibold mt-2 whitespace-nowrap ${step.color === 'orange' ? 'text-orange-500' : 'text-gray-600'}`}>
                        {step.label}
                      </p>
                    </div>
                    {i < 5 && (
                      <div className="hidden md:flex items-center mx-2">
                        <i className="ri-arrow-right-line text-orange-300 text-xl"></i>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* 부서 소개 카드 */}
            <div className="mt-20 md:mt-28">
              <div className="text-center mb-10">
                <span className="text-orange-500 text-xs font-semibold uppercase tracking-widest">Departments</span>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-2">각 부서 주요 역할</h3>
                <div className="w-8 h-1 bg-orange-500 mx-auto rounded-full mt-2"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {deptCards.map((item, idx) => (
                  <div
                    key={idx}
                    ref={el => cardRefs.current[idx] = el}
                    data-card-index={idx}
                    className={`bg-white rounded-2xl p-6 shadow-sm border hover:shadow-xl hover:scale-[1.03] transition-all duration-500 cursor-default ${
                      item.color === 'orange' ? 'border-orange-100 hover:border-orange-300' : 'border-gray-100 hover:border-gray-300'
                    } ${cardsVisible.has(idx) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 flex items-center justify-center rounded-xl transition-all duration-300 group-hover:scale-110 ${
                        item.color === 'orange' ? 'bg-orange-100' : 'bg-gray-100'
                      }`}>
                        <i className={`${item.icon} text-xl ${item.color === 'orange' ? 'text-orange-500' : 'text-gray-600'}`}></i>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-base">{item.dept}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          item.color === 'orange' ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {item.tag}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 leading-relaxed mb-4">{item.desc}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {item.tasks.map((task, ti) => (
                        <span key={ti} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                          item.color === 'orange'
                            ? 'border-orange-200 text-orange-600 bg-orange-50 hover:bg-orange-100'
                            : 'border-gray-200 text-gray-600 bg-gray-50 hover:bg-gray-100'
                        }`}>
                          {task}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 하단 CTA */}
        <div className="py-12 md:py-20 px-5 bg-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-3">
              NS-TECH와 함께하세요
            </h2>
            <p className="text-gray-500 text-sm md:text-base mb-8">
              전문화된 조직으로 설계부터 납품까지 원스톱 서비스를 제공합니다.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/#quote"
                className="px-8 py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors shadow-md whitespace-nowrap cursor-pointer text-sm"
              >
                견적 문의하기
              </Link>
              <Link
                to="/"
                className="px-8 py-3 bg-white text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors border-2 border-gray-200 whitespace-nowrap cursor-pointer text-sm"
              >
                홈으로 돌아가기
              </Link>
            </div>
          </div>
        </div>

        {/* 푸터 */}
        <footer className="bg-orange-500 text-white py-8 px-5">
          <div className="max-w-5xl mx-auto text-center">
            <img
              src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
              alt="NS-TECH Logo"
              className="h-10 w-auto mx-auto mb-4 brightness-0 invert"
            />
            <p className="text-white/80 text-xs mb-1">© 2025 NS-TECH. All rights reserved.</p>
            <p className="text-white/60 text-xs">경기도 포천시 소흘읍 소흘로 11 | Tel: 031-541-1902 | Email: nstech01@naver.com</p>
          </div>
        </footer>

        {/* 모바일 플로팅 전화 버튼 */}
        <a
          href="tel:031-541-1902"
          className="lg:hidden fixed bottom-24 right-4 z-50 flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white pl-3 pr-4 py-2.5 rounded-full shadow-xl transition-all active:scale-95 cursor-pointer"
          aria-label="전화 연결"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-phone-fill text-white text-base"></i>
          </div>
          <span className="text-xs font-bold whitespace-nowrap">전화문의</span>
        </a>

        {/* 연락처 모달 */}
        {showContactModal && (
          <div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={() => setShowContactModal(false)}
          >
            <div
              className="bg-white rounded-3xl max-w-md w-full p-6 md:p-8 relative shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowContactModal(false)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                aria-label="닫기"
              >
                <i className="ri-close-line text-gray-600 text-xl"></i>
              </button>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-customer-service-2-line text-orange-500 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">견적 문의</h3>
                <p className="text-sm text-gray-500">편하신 방법으로 연락주세요</p>
              </div>
              <div className="space-y-4 mb-6">
                {[
                  { icon: 'ri-building-line', label: '회사명', value: 'NS-TECH' },
                  { icon: 'ri-phone-line', label: '전화번호', value: '031-541-1902' },
                  { icon: 'ri-printer-line', label: '팩스', value: '031-541-1903' },
                  { icon: 'ri-mail-line', label: '이메일', value: 'nstech01@naver.com' },
                  { icon: 'ri-time-line', label: '운영시간', value: 'AM 08:00 ~ PM 18:00' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-orange-500`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{item.label}</p>
                      <p className="text-base font-semibold text-gray-900">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <a
                  href="tel:031-541-1902"
                  className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line"></i>
                  <span>전화하기</span>
                </a>
                <a
                  href="mailto:nstech01@naver.com"
                  className="flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-xl font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line"></i>
                  <span>메일보내기</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
